export const customerDetailPath = '/customer/CustomerReview/CustomerDetails'

export function parseCustomerDetailIds(query = {}) {
	const raw = query && query.ids
	if (raw == null || raw === '') {
		return []
	}
	if (Array.isArray(raw)) {
		return raw.map(item => String(item)).filter(Boolean)
	}
	const text = String(raw).trim()
	if (!text) {
		return []
	}
	if (text.startsWith('[')) {
		try {
			const parsed = JSON.parse(text)
			if (Array.isArray(parsed)) {
				return parsed.map(item => String(item)).filter(Boolean)
			}
		} catch (e) {
			// ignore invalid json
		}
	}
	return text.split(',').map(item => item.trim()).filter(Boolean)
}

export function serializeCustomerDetailIds(ids = []) {
	if (!Array.isArray(ids) || !ids.length) {
		return ''
	}
	return ids.map(item => String(item)).filter(Boolean).join(',')
}

export function normalizeCustomerDetailQuery(query = {}) {
	const result = {
		id: String(query.id != null ? query.id : ''),
		pageType: String(query.pageType != null ? query.pageType : '')
	}
	const ids = serializeCustomerDetailIds(parseCustomerDetailIds(query))
	if (ids) {
		result.ids = ids
	}
	if (query.searchData != null && query.searchData !== '') {
		result.searchData = String(query.searchData)
	}
	if (query.listApi != null && query.listApi !== '') {
		result.listApi = String(query.listApi)
	}
	return result
}

export function buildCustomerDetailQuery({ id, pageType, ids, searchData, listApi } = {}) {
	const query = {
		id: String(id != null ? id : ''),
		pageType: String(pageType != null ? pageType : '')
	}
	const idsStr = serializeCustomerDetailIds(Array.isArray(ids) ? ids : parseCustomerDetailIds({ ids }))
	if (idsStr) {
		query.ids = idsStr
	}
	if (searchData != null && searchData !== '') {
		query.searchData = typeof searchData === 'string' ? searchData : JSON.stringify(searchData)
	}
	if (listApi != null && listApi !== '') {
		query.listApi = String(listApi)
	}
	return query
}

export function buildCustomerDetailFullPath(query) {
	const q = normalizeCustomerDetailQuery(query)
	const params = new URLSearchParams()
	params.set('id', q.id)
	params.set('pageType', q.pageType)
	if (q.ids) {
		params.set('ids', q.ids)
	}
	if (q.searchData) {
		params.set('searchData', q.searchData)
	}
	if (q.listApi) {
		params.set('listApi', q.listApi)
	}
	return `${customerDetailPath}?${params.toString()}`
}

export function buildCustomerDetailTabKey(query) {
	const q = normalizeCustomerDetailQuery(query)
	return `${customerDetailPath}?id=${q.id}&pageType=${q.pageType}`
}

export function isSameCustomerDetailRoute(a, b) {
	if (!a || !b || a.path !== customerDetailPath || b.path !== customerDetailPath) {
		return false
	}
	const qa = normalizeCustomerDetailQuery(a.query)
	const qb = normalizeCustomerDetailQuery(b.query)
	return qa.id === qb.id && qa.pageType === qb.pageType
}

export function cloneRoute(route) {
	if (!route) {
		return route
	}
	const meta = route.meta ? { ...route.meta } : {}
	const isCustomerDetail = route.path === customerDetailPath
	const query = isCustomerDetail
		? normalizeCustomerDetailQuery(route.query)
		: { ...(route.query || {}) }
	return {
		fullPath: isCustomerDetail ? buildCustomerDetailFullPath(query) : route.fullPath,
		path: route.path,
		name: route.name,
		hash: route.hash,
		params: { ...(route.params || {}) },
		query,
		meta
	}
}

export function resolveTabCacheKey(snapshot) {
	if (!snapshot) {
		return ''
	}
	if (snapshot.path === customerDetailPath) {
		return buildCustomerDetailTabKey(snapshot.query)
	}
	return snapshot.path || snapshot.fullPath || ''
}

export function assignTabCacheKey(snapshot, existingKey) {
	const stableKey = existingKey || resolveTabCacheKey(snapshot)
	snapshot.cacheKey = stableKey
	return snapshot
}

export function findTabPageIndex(pages, route) {
	if (!Array.isArray(pages) || !route) {
		return -1
	}
	if (route.path === customerDetailPath) {
		return pages.findIndex(item => isSameCustomerDetailRoute(item, route))
	}
	return pages.findIndex(item => item.path === route.path)
}

export function findTabPage(pages, route) {
	const index = findTabPageIndex(pages, route)
	return index > -1 ? pages[index] : null
}

export function hasCustomerDetailTab(pages, id, pageType) {
	return findTabPageIndex(pages, {
		path: customerDetailPath,
		query: buildCustomerDetailQuery({ id, pageType })
	}) > -1
}

export function getRouteTabCacheKey(route, pages = []) {
	const page = findTabPage(pages, route)
	if (page && page.cacheKey) {
		return page.cacheKey
	}
	return resolveTabCacheKey(route)
}

export function isSameTabRoute(a, b) {
	if (!a || !b) {
		return false
	}
	if (a.path === customerDetailPath || b.path === customerDetailPath) {
		return isSameCustomerDetailRoute(a, b)
	}
	// 与 findTabPageIndex 一致：普通页按 path 识别标签，避免大 query 做 JSON.stringify
	return a.path === b.path
}

export function syncTabPageSnapshot(page, routeSnapshot) {
	if (!page || !routeSnapshot) {
		return page
	}
	const nextCacheKey = resolveTabCacheKey(routeSnapshot)
	assignTabCacheKey(routeSnapshot, nextCacheKey)
	page.fullPath = routeSnapshot.fullPath
	page.query = routeSnapshot.query
	page.params = routeSnapshot.params
	page.name = routeSnapshot.name
	page.cacheKey = nextCacheKey
	page.meta = {
		...(page.meta || {}),
		...(routeSnapshot.meta || {})
	}
	return page
}

export const CUSTOMER_DETAIL_NEIGHBOR_CTX_KEY = 'CUSTOMER_DETAIL_NEIGHBOR_CTX'

export function saveCustomerDetailNeighborContext({ listApi, searchData, pageType } = {}) {
	try {
		const payload = {
			listApi: listApi != null ? String(listApi) : '',
			pageType: pageType != null ? String(pageType) : '',
			searchData: searchData && typeof searchData === 'object' ? searchData : {}
		}
		sessionStorage.setItem(CUSTOMER_DETAIL_NEIGHBOR_CTX_KEY, JSON.stringify(payload))
	} catch (e) {
		// ignore quota / privacy errors
	}
}

export function clearCustomerDetailNeighborContext() {
	try {
		sessionStorage.removeItem(CUSTOMER_DETAIL_NEIGHBOR_CTX_KEY)
	} catch (e) {
		// ignore
	}
}

export function readCustomerDetailNeighborContext() {
	try {
		const raw = sessionStorage.getItem(CUSTOMER_DETAIL_NEIGHBOR_CTX_KEY)
		if (!raw) {
			return null
		}
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') {
			return null
		}
		return parsed
	} catch (e) {
		return null
	}
}

export function parseCustomerDetailSearchData(raw) {
	if (raw == null || raw === '') {
		return {}
	}
	if (typeof raw === 'object') {
		return { ...raw }
	}
	try {
		const parsed = JSON.parse(String(raw))
		return parsed && typeof parsed === 'object' ? parsed : {}
	} catch (e) {
		return {}
	}
}

export function buildCustomerNeighborParams(searchData = {}, id) {
	const source = searchData && typeof searchData === 'object' ? { ...searchData } : {}
	// 上下邻接口按全量筛选结果定位，不传分页参数
	delete source.pageNum
	delete source.pageSize
	const starRating = Array.isArray(source.starRating)
		? source.starRating.join(',')
		: source.starRating
	const params = {
		...source,
		starRating,
		id
	}
	Object.keys(params).forEach((key) => {
		if (params[key] === undefined) {
			delete params[key]
		}
	})
	return params
}
